import type { ApiResponse, Profile, ProfileListItem, Job, JobListItem, MatchResult, TelegramStatus } from '../types/api'
import { getToken } from '../context/AuthContext'

const BASE = import.meta.env.VITE_API_URL || '/api'

function getAuthHeaders(): Record<string, string> {
  const token = getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${endpoint}`, {
    headers: { ...getAuthHeaders(), ...(options.headers as Record<string, string> ?? {}) },
    ...options,
  })

  if (res.status === 401) {
    // Token expired or invalid — clear auth and let the app handle redirect
    localStorage.removeItem('nymo_auth_token')
    window.location.href = '/login'
    throw new Error('Unauthorized')
  }

  const json = await res.json()
  if (!res.ok) throw new Error(json.error ?? json.message ?? 'Request failed')
  return json
}

async function upload<T>(endpoint: string, file: File): Promise<T> {
  const form = new FormData()
  form.append('file', file)

  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${BASE}${endpoint}`, {
    method: 'POST',
    body: form,
    headers,
  })

  if (res.status === 401) {
    localStorage.removeItem('nymo_auth_token')
    window.location.href = '/login'
    throw new Error('Unauthorized')
  }

  const json = await res.json()
  if (!res.ok) throw new Error(json.error ?? 'Upload failed')
  return json
}

export const api = {
  profiles: {
    list: () => request<ApiResponse<ProfileListItem[]>>('/profiles'),
    get: (id: string) => request<ApiResponse<Profile>>(`/profiles/${id}`),
    createBasic: (data: Record<string, unknown>) =>
      request<ApiResponse<Profile>>('/profiles/basic', { method: 'POST', body: JSON.stringify(data) }),
    uploadFile: (file: File) => upload<ApiResponse<Profile>>('/profiles/file', file),
    update: (id: string, data: string) =>
      request<ApiResponse<Profile>>(`/profiles/${id}`, { method: 'PUT', body: JSON.stringify({ data }) }),
    updateFrontmatter: (id: string, fields: Record<string, unknown>) =>
      request<ApiResponse<Profile>>(`/profiles/${id}/frontmatter`, { method: 'PATCH', body: JSON.stringify(fields) }),
    chat: (id: string, message: string) =>
      request<ApiResponse<{ reply: string }>>(`/profiles/${id}/chat`, { method: 'POST', body: JSON.stringify({ message }) }),
  },
  jobs: {
    list: () => request<ApiResponse<JobListItem[]>>('/jobs'),
    get: (id: string) => request<ApiResponse<Job>>(`/jobs/${id}`),
    createBasic: (data: Record<string, unknown>) =>
      request<ApiResponse<Job>>('/jobs/basic', { method: 'POST', body: JSON.stringify(data) }),
    uploadFile: (file: File) => upload<ApiResponse<Job>>('/jobs/file', file),
    update: (id: string, data: string) =>
      request<ApiResponse<Job>>(`/jobs/${id}`, { method: 'PUT', body: JSON.stringify({ data }) }),
    updateFrontmatter: (id: string, fields: Record<string, unknown>) =>
      request<ApiResponse<Job>>(`/jobs/${id}/frontmatter`, { method: 'PATCH', body: JSON.stringify(fields) }),
    matches: {
      create: (id: string, limit = 10) =>
        request<ApiResponse<{ job_id: string; created_at: string; title: string; employer: string; matches: MatchResult[] }>>(`/jobs/${id}/matches?create=true&limit=${limit}`),
      getLatest: (id: string) =>
        request<ApiResponse<{ job_id: string; created_at: string; title: string; employer: string; matches: MatchResult[] }>>(`/jobs/${id}/matches`),
    },
    chat: (id: string, message: string) =>
      request<ApiResponse<{ reply: string }>>(`/jobs/${id}/chat`, { method: 'POST', body: JSON.stringify({ message }) }),
  },
  telegram: {
    getStatus: (profileId: string) =>
      request<ApiResponse<TelegramStatus>>(`/telegram/status/${profileId}`),
    sendMessage: (profileId: string, message: string) =>
      request<ApiResponse<{ sent: boolean }>>(`/telegram/profiles/${profileId}/send`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      }),
    sendQuestions: (jobId: string, profileId: string) =>
      request<ApiResponse<{ sent: boolean }>>(`/telegram/jobs/${jobId}/questions/${profileId}`, {
        method: 'POST',
      }),
  },
}
